import React, { use } from 'react';

import {
  getAllCityClubsForClubFinder,
  getAllClubsForClubFinder,
  getClubInformation,
} from 'src/content/club-look-ups-by-id';
import type { City } from 'src/enums/city';
import type { ClubInformation } from 'src/enums/club-information';
import { CLUBS_INFORMATION } from 'src/enums/club-information';
import type { ClubFinderClubNode, CmsClubDetails } from 'src/helpers/club-finder-helper';
import { parseClubDetailsData } from 'src/helpers/club-finder-helper';

import { ClubFinderPageDataContext } from './club-finder-page-data-context';

export enum ClubFinderClubsCmsProviderFilterType {
  DEFAULT,
  CLUB,
  CITY,
  ALL,
}

type ClubFinderClubsCmsProviderFilter =
  | {
      type: ClubFinderClubsCmsProviderFilterType.DEFAULT;
    }
  | { type: ClubFinderClubsCmsProviderFilterType.CLUB; siteId: string }
  | {
      type: ClubFinderClubsCmsProviderFilterType.CITY;
      city: City;
    }
  | {
      type: ClubFinderClubsCmsProviderFilterType.ALL;
    };

export const getClubsByFilter = (
  filter: ClubFinderClubsCmsProviderFilter,
  siteIdsForClubFinder: readonly string[]
): readonly ClubInformation[] => {
  switch (filter.type) {
    case ClubFinderClubsCmsProviderFilterType.DEFAULT:
      return getAllClubsForClubFinder(siteIdsForClubFinder);
    case ClubFinderClubsCmsProviderFilterType.CLUB:
      return [getClubInformation(filter.siteId)];
    case ClubFinderClubsCmsProviderFilterType.CITY:
      return getAllCityClubsForClubFinder(filter.city, siteIdsForClubFinder);
    case ClubFinderClubsCmsProviderFilterType.ALL:
      return CLUBS_INFORMATION;
  }
};

type ClubFinderClubsPageQueryData = {
  clubsContent?: {
    nodes: readonly ClubFinderClubNode[];
  };
  clubList?: {
    childClubJson?: { siteIds?: readonly string[] | null } | null;
  } | null;
};

const getSiteIdsForClubFinder = (data: ClubFinderClubsPageQueryData): readonly string[] => {
  const siteIds = data.clubList?.childClubJson?.siteIds;
  if (siteIds === null || siteIds === undefined) {
    throw new Error('Club finder site-id list missing from page query.');
  }
  return siteIds;
};

export const unwrapClubFinderDataFromPageQuery = (
  data: unknown,
  filter: ClubFinderClubsCmsProviderFilter
): CmsClubDetails[] => {
  const pageData = data as ClubFinderClubsPageQueryData;
  if (pageData.clubsContent === null || pageData.clubsContent === undefined) {
    throw new Error('Club finder data missing from page query.');
  }

  const clubRelativeDirectories =
    filter.type === ClubFinderClubsCmsProviderFilterType.CLUB
      ? Array.from(new Set(pageData.clubsContent.nodes.map((n) => n.relativeDirectory)))
      : getClubsByFilter(filter, getSiteIdsForClubFinder(pageData)).map(
          (clubInformation) => `${clubInformation.hyphenatedClubName}/club`
        );

  return parseClubDetailsData(pageData.clubsContent.nodes, clubRelativeDirectories);
};

type ClubFinderClubsCmsProviderProps = {
  filter?: ClubFinderClubsCmsProviderFilter;
  children: (clubs: CmsClubDetails[]) => React.ReactNode;
};

export const ClubFinderClubsCmsProvider = ({
  filter = { type: ClubFinderClubsCmsProviderFilterType.DEFAULT },
  children,
}: ClubFinderClubsCmsProviderProps) => {
  const pageData = use(ClubFinderPageDataContext);
  if (pageData === null || pageData === undefined) {
    throw new Error(
      'ClubFinderClubsCmsProvider must be rendered inside a <ClubFinderPageDataProvider />.'
    );
  }

  return children(unwrapClubFinderDataFromPageQuery(pageData, filter));
};

type ClubFinderPageDataProviderProps = {
  pageData: unknown;
  children: React.ReactNode;
};

export const ClubFinderPageDataProvider = ({
  pageData,
  children,
}: ClubFinderPageDataProviderProps) => (
  <ClubFinderPageDataContext.Provider value={pageData}>
    {children}
  </ClubFinderPageDataContext.Provider>
);
